import PropTypes from 'prop-types';
import React, { memo, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import {
  Card, CardBody, Col, Row,
} from 'reactstrap';

import { UNTRADED_ENERGY_KEY } from 'src/util/constants';

import { avatar, opacity, ring } from '../helpers/cards';

/**
 * Individual card item component - memoized to prevent unnecessary re-renders
 */
const CardItem = memo(({
  datum,
  cardFunc,
  hoverFunc,
  selectedFunc,
  hoverKeys,
  selectedKeys,
}) => {
  const {
    buy, key, sell,
    tradeType, user,
  } = datum;

  const cardOpacity = opacity(key, hoverKeys, selectedKeys);
  const cardRing = ring(buy, sell, tradeType === UNTRADED_ENERGY_KEY);
  const cardAvatar = avatar(user || null, cardRing, tradeType);
  const cardId = tradeType ? ` trade-${tradeType}` : '';

  const handleMouseEnter = useCallback(() => {
    hoverFunc?.(key, true);
  }, [hoverFunc, key]);

  const handleMouseLeave = useCallback(() => {
    hoverFunc?.(key, false);
  }, [hoverFunc, key]);

  const handleClick = useCallback(() => {
    selectedFunc?.(key);
  }, [selectedFunc, key]);

  return (
    <Col className={`mb-4 cards${cardId}`} sm="6" lg="4" xl="3">
      <Card
        className="h-100"
        style={{ opacity: cardOpacity, cursor: 'pointer' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <CardBody>
          <Row className="flex-nowrap">
            <Col xs="auto">
              {cardAvatar}
            </Col>
            <Col xs="auto" className="w-75">
              {cardFunc(datum)}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
});

CardItem.displayName = 'CardItem';

CardItem.propTypes = {
  datum: PropTypes.shape({
    buy: PropTypes.shape({
      count: PropTypes.number,
      value: PropTypes.number,
      volume: PropTypes.number,
    }),
    key: PropTypes.string.isRequired,
    sell: PropTypes.shape({
      count: PropTypes.number,
      value: PropTypes.number,
      volume: PropTypes.number,
    }),
    tradeType: PropTypes.string,
    user: PropTypes.shape({
      uuid: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      email: PropTypes.string,
    }),
  }).isRequired,
  cardFunc: PropTypes.func.isRequired,
  hoverFunc: PropTypes.func,
  selectedFunc: PropTypes.func,
  hoverKeys: PropTypes.arrayOf(PropTypes.string),
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
};

CardItem.defaultProps = {
  hoverFunc: null,
  selectedFunc: null,
  hoverKeys: [],
  selectedKeys: [],
};

/**
 * Cards component - renders a grid of memoized card items
 * @param {any} props
 * @returns {React.ReactComponentElement} - Cards component
 */
function Cards(props) {
  const {
    cardFunc,
    data,
    hoverFunc,
    hoverKeys,
    selectedFunc,
    selectedKeys,
  } = props;
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Row className="mt-3">
      {data.map((datum) => {
        const { key } = datum;

        return (
          <CardItem
            key={key}
            datum={datum}
            cardFunc={cardFunc}
            hoverFunc={hoverFunc}
            selectedFunc={selectedFunc}
            hoverKeys={hoverKeys}
            selectedKeys={selectedKeys}
          />
        );
      })}
    </Row>
  );
}

Cards.propTypes = {
  cardFunc: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    buy: PropTypes.shape({
      count: PropTypes.number,
      value: PropTypes.number,
      volume: PropTypes.number,
    }),
    key: PropTypes.string,
    sell: PropTypes.shape({
      count: PropTypes.number,
      value: PropTypes.number,
      volume: PropTypes.number,
    }),
    tradeType: PropTypes.string,
    user: PropTypes.shape({
      uuid: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      email: PropTypes.string,
    }),
  })),
  hoverFunc: PropTypes.func,
  hoverKeys: PropTypes.arrayOf(
    PropTypes.string,
  ),
  selectedFunc: PropTypes.func,
  selectedKeys: PropTypes.arrayOf(
    PropTypes.string,
  ),
};

Cards.defaultProps = {
  data: [],
  hoverFunc: null,
  hoverKeys: [],
  selectedFunc: null,
  selectedKeys: [],
};

export default injectIntl(memo(Cards));
